import { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollText, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Navigation, UserCheck, Flag, Pencil, RotateCcw, ShieldOff, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const PAGE_SIZE = 50;

const ACTION_META = {
  ride_edit: { label: 'Ride Edited', color: 'bg-blue-100 text-blue-700', Icon: Pencil },
  arrived_at_pickup: { label: 'Arrived at Pickup', color: 'bg-indigo-100 text-indigo-700', Icon: Navigation },
  picked_up_customer: { label: 'Picked Up Customer', color: 'bg-purple-100 text-purple-700', Icon: UserCheck },
  arrived_at_dropoff: { label: 'Completed Ride', color: 'bg-emerald-100 text-emerald-700', Icon: Flag },
  reset_tracking: { label: 'Tracking Reset', color: 'bg-amber-100 text-amber-700', Icon: RotateCcw },
};

const FIELD_LABELS = {
  assigned_driver_id: 'Assigned Driver ID',
  assigned_driver_name: 'Assigned Driver',
  date_time: 'Date & Time',
  pickup_location: 'Pickup',
  dropoff_location: 'Dropoff',
  customer_name: 'Customer',
  price: 'Price',
  payment_to_partner: 'Partner Price',
  driver_commission: 'Commission',
  vehicle_type: 'Vehicle Category',
  vehicle_plate: 'Vehicle Plate',
  status: 'Status',
  notes: 'Notes',
  partner: 'Partner',
};

const formatVal = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const formatDT = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export default function ActionLog() {
  const { user, token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [actors, setActors] = useState([]);
  const [actionType, setActionType] = useState('all');
  const [actorId, setActorId] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const fetchLogs = useCallback(async () => {
    if (!user?.is_super_admin) return;
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (actionType !== 'all') params.action_type = actionType;
      if (actorId !== 'all') params.actor_id = actorId;
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/admin-action-logs`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setActors(res.data.actors || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load action log');
    } finally {
      setLoading(false);
    }
  }, [token, actionType, actorId, search, dateFrom, dateTo, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (!user?.is_super_admin) {
    return (
      <div className="p-8 flex flex-col items-center justify-center h-full text-center" data-testid="action-log-forbidden">
        <ShieldOff className="h-12 w-12 text-slate-300 mb-3" />
        <p className="text-slate-600 font-medium">Only super admins can view the action log</p>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8" data-testid="action-log-page">
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
          <ScrollText className="h-7 w-7 text-indigo-600" />
          Admin Action Log
        </h1>
        <p className="text-slate-500 mt-1">History of admins editing rides or pressing ride actions for other drivers</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Action</label>
          <Select value={actionType} onValueChange={(v) => { setActionType(v); setPage(0); }}>
            <SelectTrigger className="w-52 h-10" data-testid="action-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="ride_edit">Ride Edited</SelectItem>
              <SelectItem value="arrived_at_pickup">Arrived at Pickup</SelectItem>
              <SelectItem value="picked_up_customer">Picked Up Customer</SelectItem>
              <SelectItem value="arrived_at_dropoff">Completed Ride</SelectItem>
              <SelectItem value="reset_tracking">Tracking Reset</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Admin</label>
          <Select value={actorId} onValueChange={(v) => { setActorId(v); setPage(0); }}>
            <SelectTrigger className="w-52 h-10" data-testid="actor-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All admins</SelectItem>
              {actors.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs font-medium text-slate-500 mb-1 block">Search (customer, admin, driver)</label>
          <div className="flex gap-2">
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput.trim()); setPage(0); } }}
              placeholder="e.g. customer name..."
              className="h-10"
              data-testid="action-log-search-input"
            />
            <Button
              onClick={() => { setSearch(searchInput.trim()); setPage(0); }}
              className="h-10 bg-indigo-600 hover:bg-indigo-700"
              data-testid="action-log-search-btn"
            >
              <Search className="h-4 w-4" />
            </Button>
            {search && (
              <Button
                variant="outline"
                className="h-10"
                onClick={() => { setSearch(''); setSearchInput(''); setPage(0); }}
                data-testid="action-log-search-clear"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">From</label>
          <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(0); }} className="h-10 w-40" data-testid="action-log-date-from" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">To</label>
          <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(0); }} className="h-10 w-40" data-testid="action-log-date-to" />
        </div>
        <div className="ml-auto text-sm text-slate-500 pb-2" data-testid="action-log-total">{total} entries</div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Admin</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Ride</th>
                <th className="px-4 py-3">Assigned Driver</th>
                <th className="px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No logged actions yet</td></tr>
              ) : logs.map(log => {
                const meta = ACTION_META[log.action_type] || { label: log.action_type, color: 'bg-slate-100 text-slate-600', Icon: ScrollText };
                const isOpen = expanded === log.id;
                const changeEntries = log.changes ? Object.entries(log.changes) : [];
                return (
                  <tr key={log.id} className="align-top hover:bg-slate-50" data-testid={`action-log-row-${log.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600">{formatDT(log.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{log.actor_name || '—'}</div>
                      <div className="text-xs text-slate-400">{log.actor_email}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.color}`}>
                        <meta.Icon className="h-3 w-3" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{log.customer_name || '—'}</div>
                      <div className="text-xs text-slate-400">{formatDT(log.ride_date_time)}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {log.assigned_driver_name || '—'}
                      {log.own_ride && <span className="ml-2 text-xs text-emerald-600">(own ride)</span>}
                    </td>
                    <td className="px-4 py-3">
                      {changeEntries.length > 0 ? (
                        <div>
                          <button
                            onClick={() => setExpanded(isOpen ? null : log.id)}
                            className="flex items-center gap-1 text-indigo-600 hover:underline text-xs font-medium"
                            data-testid={`toggle-changes-${log.id}`}
                          >
                            {changeEntries.length} field{changeEntries.length > 1 ? 's' : ''} changed
                            {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          </button>
                          {isOpen && (
                            <div className="mt-2 space-y-1 bg-slate-50 rounded-lg p-2 max-w-md">
                              {changeEntries.map(([field, ch]) => (
                                <div key={field} className="text-xs">
                                  <span className="font-medium text-slate-700">{FIELD_LABELS[field] || field}:</span>{' '}
                                  <span className="text-red-500 line-through break-all">{formatVal(ch.old)}</span>
                                  {' → '}
                                  <span className="text-emerald-600 break-all">{formatVal(ch.new)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Action pressed for another driver's ride</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
          <span className="text-xs text-slate-500">Page {page + 1} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="action-log-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="action-log-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
